/**
 * Whitelabel Utilities
 * Functions for loading, caching, and applying whitelabel configuration
 */

import apiUtilsSmsmedia from './apiUtilsSmsmedia';

const WHITELABEL_CACHE_KEY = 'whitelabelConfig';
const WHITELABEL_CACHE_TIMESTAMP_KEY = 'whitelabelConfigTimestamp';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Load whitelabel configuration from API by domain
 * @param {string} domain - The domain to fetch configuration for
 * @returns {Promise<Object|null>} Whitelabel configuration object or null
 */
export const loadWhitelabelConfig = async (domain) => {
    try {
        console.log(`Loading whitelabel config for domain: ${domain}`);

        // Call the API endpoint using apiUtilsSmsmedia
        const response = await apiUtilsSmsmedia.callApi(`/api/whitelabel/config/${domain}`, 'GET');
        
        // If response is already the data object (not wrapped in .data)
        const config = response.data || response;

        console.log('Whitelabel config loaded:', config);

        if (config) {
            // Cache the configuration
            cacheWhitelabelConfig(config);
            return config;
        }

        return null;
    } catch (error) {
        console.error('Error loading whitelabel config:', error);
        // Return cached config if available on error
        return getCachedWhitelabel();
    }
};

/**
 * Get cached whitelabel configuration from localStorage
 * @returns {Object|null} Cached whitelabel configuration or null
 */
export const getCachedWhitelabel = () => {
    try {
        const cached = localStorage.getItem(WHITELABEL_CACHE_KEY);
        const timestamp = localStorage.getItem(WHITELABEL_CACHE_TIMESTAMP_KEY);

        if (!cached || !timestamp) {
            return null;
        }

        // Check if cache is still valid
        const now = Date.now();
        const cacheAge = now - parseInt(timestamp, 10);

        if (cacheAge > CACHE_DURATION_MS) {
            // Cache expired
            clearWhitelabelCache();
            return null;
        }

        return JSON.parse(cached);
    } catch (error) {
        console.error('Error reading whitelabel cache:', error);
        return null;
    }
};

/**
 * Cache whitelabel configuration to localStorage
 * @param {Object} config - Whitelabel configuration object
 */
export const cacheWhitelabelConfig = (config) => {
    try {
        localStorage.setItem(WHITELABEL_CACHE_KEY, JSON.stringify(config));
        localStorage.setItem(WHITELABEL_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
        console.error('Error caching whitelabel config:', error);
    }
};

/**
 * Clear whitelabel cache from localStorage
 */
export const clearWhitelabelCache = () => {
    try {
        localStorage.removeItem(WHITELABEL_CACHE_KEY);
        localStorage.removeItem(WHITELABEL_CACHE_TIMESTAMP_KEY);
    } catch (error) {
        console.error('Error clearing whitelabel cache:', error);
    }
};

/**
 * Update document metadata with whitelabel configuration
 * @param {Object} config - Whitelabel configuration object
 */
export const updateDocumentMeta = (config) => {
    if (!config) return;

    try {
        // Update document title
        if (config.title) {
            document.title = config.title;
        }

        // Update favicon
        if (config.iconUrl) {
            // Remove existing favicon links
            const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
            existingFavicons.forEach(link => link.remove());

            // Add new favicon
            const favicon = document.createElement('link');
            favicon.rel = 'icon';
            favicon.type = 'image/x-icon';
            favicon.href = config.iconUrl;
            document.head.appendChild(favicon);

            // Add apple touch icon
            const appleTouchIcon = document.createElement('link');
            appleTouchIcon.rel = 'apple-touch-icon';
            appleTouchIcon.href = config.iconUrl;
            document.head.appendChild(appleTouchIcon);
        }

        // Inject CSS variables for colors
        if (config.primaryColor || config.secondaryColor) {
            let styleElement = document.getElementById('whitelabel-styles');
            
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = 'whitelabel-styles';
                document.head.appendChild(styleElement);
            }

            const cssVariables = `:root {
                ${config.primaryColor ? `--primary-color: ${config.primaryColor};` : ''}
                ${config.secondaryColor ? `--secondary-color: ${config.secondaryColor};` : ''}
            }`;

            styleElement.textContent = cssVariables;
        }
    } catch (error) {
        console.error('Error updating document metadata:', error);
    }
};

/**
 * Get default whitelabel configuration
 * @returns {Object} Default configuration object
 */
export const getDefaultWhitelabelConfig = () => {
    return {
        domainName: window.location.hostname,
        organisationName: 'Edutron School ERP',
        title: 'Edutron School ERP',
        logoUrl: null,
        iconUrl: null,
        supportEmail: 'support@edutron.com',
        supportPhone: '',
        supportAddress: '',
        primaryColor: '#1976d2',
        secondaryColor: '#dc004e',
        productType: 'Edutron School ERP',
        status: 'Active'
    };
};

/**
 * Validate whitelabel configuration
 * @param {Object} config - Configuration object to validate
 * @returns {boolean} Whether the configuration is valid
 */
export const isValidWhitelabelConfig = (config) => {
    if (!config || typeof config !== 'object') {
        return false;
    }

    // Check for required fields
    const requiredFields = ['domainName', 'organisationName', 'primaryColor', 'secondaryColor'];
    
    for (const field of requiredFields) {
        if (!config[field]) {
            return false;
        }
    }

    // Validate color formats (basic hex validation)
    const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
    if (!hexColorRegex.test(config.primaryColor) || !hexColorRegex.test(config.secondaryColor)) {
        return false;
    }

    return true;
};

export default {
    loadWhitelabelConfig,
    getCachedWhitelabel,
    cacheWhitelabelConfig,
    clearWhitelabelCache,
    updateDocumentMeta,
    getDefaultWhitelabelConfig,
    isValidWhitelabelConfig
};
